import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Typography, TypographyProps } from "@mui/material";

type ExpandableTextProps = {
  children: ReactNode;
  collapsedLines?: number;
  variant?: TypographyProps["variant"];
  className?: string;
};

const ExpandableText = ({
  children,
  collapsedLines = 10,
  variant = "body2",
  className,
}: ExpandableTextProps) => {
  const textRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const checkOverflow = () => {
    const element = textRef.current;

    if (!element) {
      return;
    }

    setHasOverflow(element.scrollHeight > element.clientHeight + 1);
  };

  useLayoutEffect(() => {
    checkOverflow();
  }, [children, collapsedLines]);

  useEffect(() => {
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  return (
    <>
      <Typography
        ref={textRef}
        variant={variant}
        className={className}
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "unset" : collapsedLines,
        }}
      >
        {children}
      </Typography>

      {hasOverflow && (
        <Typography
          component="span"
          variant="body2"
          color="primary"
          sx={{
            cursor: "pointer",
            fontWeight: 600,
            userSelect: "none",
          }}
          onClick={() => setExpanded(prev => !prev)}
        >
          {expanded ? "Read less" : "Read more"}
        </Typography>
      )}
    </>
  );
};

export default ExpandableText;
